import { RootState } from '..';
import { Module } from "vuex";
import { surveysApi, mapApi, SurveyIndex, Survey, ApiException, SurveyAnswer } from '@/services/api';

export interface SurveysState {
  list: Survey[];
  index: SurveyIndex | null;
}

// Mutation types
const SET_SURVEYS = 'SET_SURVEYS';
const SET_SURVEY_INDEX = 'SET_SURVEY_INDEX';

const SurveysModule: Module<SurveysState, RootState> = {
  namespaced: true,
  state() {
    return {
      list: [],
      index: null,
    }
  },
  mutations: {
    [SET_SURVEYS](state, payload) {
      state.list = payload;
    },
    [SET_SURVEY_INDEX](state, payload) {
      console.log('Committing survey index:', payload);
      state.index = payload;
    }
  },
  actions: {
    /**
     * Fetch and bind surveys list
     * Gets surveys created before 2.5 hours ago
     */
    async bindSurveys({ commit }) {
      const d = new Date();
      d.setHours(d.getHours() - 2.5);
      
      try {
        const surveys = await surveysApi.getSurveys(d.toISOString());
        commit(SET_SURVEYS, surveys);
      } catch (error) {
        console.error('Failed to fetch surveys:', error);
        throw error;
      }
    },

    /**
     * Fetch and bind survey index for current user
     * Contains answered surveys, words (answers), and visited questions
     */
    async bindSurveyIndex({ commit, rootGetters }) {
      const userId = rootGetters.user.uid;
      
      try {
        const index = await surveysApi.getSurveyIndex(userId);
        commit(SET_SURVEY_INDEX, index);
      } catch (error) {
        if (error instanceof ApiException && error.status === 404) {
          // Index doesn't exist yet, set default values
          commit(SET_SURVEY_INDEX, {
            answered: [],
            surveyInfoRead: false,
            words: {},
          });
        } else {
          console.error('Failed to fetch survey index:', error);
          throw error;
        }
      }
    },

    /**
     * Mark that user has read the survey info
     */
    async setSurveyInfoRead({ rootGetters }) {
      const userId = rootGetters.user.uid;
      await surveysApi.setSurveyInfoRead(userId);
      await this.dispatch('surveys/bindSurveyIndex');
    },

    /**
     * Store survey question answer in localStorage
     * Answers are buffered locally until survey is submitted
     */
    answerSurveyQuestion(context, payload: { id: string; question: number; answer: SurveyAnswer }) {
      localStorage.setItem(
        `lidate.survey.${payload.id}.${payload.question}`, 
        JSON.stringify(payload.answer)
      );
    },

    /**
     * Get statistics for a specific question (e.g., for aggregated maps)
     */
    async getQuestionStats(_, questionId: number) {
      return await mapApi.getQuestionStats(questionId);
    },

    /**
     * Submit all buffered answers for a survey
     * Reads from localStorage, sends to backend, then clears localStorage
     * Backend automatically adds surveyId to user's answered list
     */
    async persistSurveyAnswers({ rootGetters }, payload: { id: string }) {
      const answers: SurveyAnswer[] = Object
        .keys(localStorage)
        .filter(k => k.indexOf(`lidate.survey.${payload.id}`) === 0)
        .sort()
        .map(k => {
          const v = localStorage.getItem(k);
          localStorage.removeItem(k);
          if (v) {
            return JSON.parse(v);
          } else {
            return null;
          }
        });

      const userId = rootGetters.user.uid;
      await surveysApi.submitSurveyAnswers(payload.id, userId, answers);
      
      // Note: Backend automatically updates the answered list
      // No need to manually update state.index.answered here
    }
  },
  getters: {
    /**
     * Get words with answers from survey index
     */
    words(state) {
      if (!state.index || !state.index.words) {
        return {};
      }
      
      return state.index.words;
    },

    /**
     * Get surveys that haven't been answered yet
     * Backend dynamically evaluates the answered list
     */
    incompleteSurveys(state) {
      if (!state.index) {
        return state.list;
      }
      
      const answered = state.index.answered || [];
      return state.list.filter((s) => answered.indexOf(s.id) === -1);
    },

    /**
     * Get a list of not vivited maps
     */
    newMaps(state, getters) {
      const newMaps: string[] = [];
      const words = getters.words;
      
      for (const qid in words) {
        const question = words[qid];
        if (question.visited === 0) {
          for (const answer of question.answers) {
            if (answer.used === 0 && answer.map && newMaps.indexOf(answer.map) === -1) {
              newMaps.push(answer.map);
            }
          }
        }
      }
      
      return newMaps;
    }
  }
}

export default SurveysModule